import { z } from 'zod';
import { OffsetPageQuery, offsetPage } from './paging.js';
/**
 * Контракты раздела «Бухгалтерия» (`/api/accounting/*`) — шлюз admin-api к
 * внутреннему API НДС-учёта wit-payment-service. Единственный источник
 * истины, общий для роутов admin-api и модуля `admin-module-accounting`.
 * Wire-схемы самой платёжки сюда НЕ попадают: они живут в одном файле
 * адаптера `packages/accounting/src/http-accounting.ts`, а здесь — то, что
 * видит админка.
 *
 * ДЕНЬГИ — ДЕСЯТИЧНЫЕ СТРОКИ (`'4791.37'`), не minor-единицы и не float:
 * «без НДС» оператор вводит в ROS до цента; UI форматирует через
 * `formatNumberRu`, не пересчитывая.
 *
 * Поля, которых может не быть (нет данных, старый admin-api в окне выката,
 * поле добавлено позже), — `.nullable().default(null)`, читатели проверяют
 * `!= null`.
 *
 * Тела запросов — `.strict()`: опечатка в имени поля на записи должна давать
 * 400, а не молча теряться.
 */
/** Квартал `YYYY-Qn`. Одна граница для `:quarter` в пути и `before` в query. */
export const AccountingQuarterId = z
    .string()
    .regex(/^\d{4}-Q[1-4]$/, { message: 'Квартал — в формате ГГГГ-Qn, например 2026-Q3' });
/** Регион-подмодуль платёжки (`eu-oss`, `uk`, …): строчные латиница, цифры, дефис. */
export const AccountingRegionId = z.string().regex(/^[a-z0-9][a-z0-9-]{0,39}$/, {
    message: 'Неверный идентификатор региона',
});
/** Десятичная строка со знаком. */
export const AccountingDecimal = z.string().regex(/^-?\d+(\.\d+)?$/);
/** Страна ISO-3166 alpha-2 (`DE`, `GB`). */
export const AccountingCountry = z.string().regex(/^[A-Z]{2}$/, {
    message: 'Страна — двухбуквенный код ISO, например DE',
});
/** `draft` — посчитан; `filed` — подан, суммы зафиксированы; `not_computed` — не посчитан. */
export const AccountingRegionStatus = z.enum(['draft', 'filed', 'not_computed']);
/** `unresolved` — возврат без найденной исходной оплаты (в итоги не входит). */
export const AccountingLineType = z.enum(['payment', 'refund', 'unresolved']);
export const AccountingCountrySource = z.enum(['customer_address', 'billing', 'card', 'none']);
export const AccountingPaymentKind = z.enum(['renewal', 'first_payment', 'one_time']);
/** `whole_pounds` — box 6 UK: целые фунты, отбрасывание пенсов (к нулю). */
export const AccountingBoxUnit = z.enum(['money', 'whole_pounds']);
export const AccountingRecalcOutcome = z.enum(['recalculated', 'skipped_filed']);
const Count = z.number().int().nonnegative();
const Iso = z.string().datetime();
// ---------------------------------------------------------------------------
// GET /health
// ---------------------------------------------------------------------------
/**
 * `configured: false` — admin-api запущен без `ACCOUNTING_API_BASE_URL` /
 * `ACCOUNTING_API_TOKEN`. `reachable: false` при `configured: true` — адрес
 * задан, но платёжка не ответила.
 */
export const AccountingHealthResponse = z.object({
    configured: z.boolean(),
    reachable: z.boolean().nullable().default(null),
});
// ---------------------------------------------------------------------------
// GET /regions
// ---------------------------------------------------------------------------
export const AccountingRegionDTO = z.object({
    id: AccountingRegionId,
    label: z.string().min(1),
    currency: z.string().min(1),
    /** RU-подсказка, как подавать; пустая строка — подсказки нет. */
    filingHint: z.string(),
});
export const AccountingRegionsResponse = z.object({
    regions: z.array(AccountingRegionDTO),
});
// ---------------------------------------------------------------------------
// GET /quarters
// ---------------------------------------------------------------------------
/** Листание назад: `before` — кварталы строго раньше этого. */
export const AccountingQuarterListQuery = z.object({
    limit: z.coerce.number().int().min(1).max(40).default(8),
    before: AccountingQuarterId.optional(),
});
export const AccountingQuarterTotalsDTO = z.object({
    net: AccountingDecimal,
    vat: AccountingDecimal,
    /** «К вводу»: ЕС — Σ по странам от max(net страны, 0); UK — box 5. */
    toEnter: AccountingDecimal,
});
export const AccountingQuarterRegionSummaryDTO = z.object({
    region: AccountingRegionId,
    status: AccountingRegionStatus,
    computedAt: Iso.nullable().default(null),
    filedAt: Iso.nullable().default(null),
    currency: z.string().min(1),
    /** null — регион не посчитан. */
    totals: AccountingQuarterTotalsDTO.nullable().default(null),
    warningsCount: Count,
});
export const AccountingQuarterSummaryDTO = z.object({
    quarter: AccountingQuarterId,
    isCurrent: z.boolean(),
    regions: z.array(AccountingQuarterRegionSummaryDTO),
});
export const AccountingQuarterListResponse = z.object({
    quarters: z.array(AccountingQuarterSummaryDTO),
    /** Текущий (незакрытый) квартал по часам платёжки. */
    current: AccountingQuarterId,
});
// ---------------------------------------------------------------------------
// GET /quarters/:quarter
// ---------------------------------------------------------------------------
export const AccountingReportTotalsDTO = z.object({
    lines: Count,
    payments: Count,
    refunds: Count,
    grossUsd: AccountingDecimal,
    /** Брутто в валюте региона. */
    gross: AccountingDecimal,
    net: AccountingDecimal,
    vat: AccountingDecimal,
    toEnter: AccountingDecimal,
    /** НДС корректировок прошлых периодов (≤ 0); null — корректировок нет. */
    correctionsVat: AccountingDecimal.nullable().default(null),
    /** Итого к уплате: `vat` + `correctionsVat`; null — не прислано. */
    payable: AccountingDecimal.nullable().default(null),
});
/** Брутто USD по виду платежа. */
export const AccountingByKindDTO = z.object({
    renewal: AccountingDecimal.nullable().default(null),
    firstPayment: AccountingDecimal.nullable().default(null),
    oneTime: AccountingDecimal.nullable().default(null),
});
export const AccountingReportRowDTO = z.object({
    /**
     * Страна (`DE`), `страна:ставка` при смене ставки в квартале, месяц `YYYY-MM`
     * у UK (`country` = `GB`). Не обязательно страна — показывать `label`.
     */
    key: z.string().min(1),
    country: z.string().nullable().default(null),
    label: z.string(),
    /** Код для портала (`EL` для Греции); null — не нужен. */
    displayCode: z.string().nullable().default(null),
    /** Ставка НДС в процентах (`'21'`, `'25.5'`), не доля. */
    rate: AccountingDecimal.nullable().default(null),
    payments: Count,
    refunds: Count,
    grossUsd: AccountingDecimal,
    gross: AccountingDecimal,
    /** «Без НДС — ввести в ROS». */
    net: AccountingDecimal,
    vat: AccountingDecimal,
    toEnter: AccountingDecimal,
    /** Возвратов больше, чем оплат: в портал вводится 0. */
    negativeBase: z.boolean(),
    byKind: AccountingByKindDTO.nullable().default(null),
    /**
     * Квартал исходных продаж (`YYYY-Qn`) — у строк региона корректировок
     * прошлых периодов (`eu-oss-corrections`); у обычных регионов null.
     */
    period: AccountingQuarterId.nullable().default(null),
});
export const AccountingReportBoxDTO = z.object({
    box: z.number().int().min(1).max(9),
    label: z.string(),
    value: AccountingDecimal,
    unit: AccountingBoxUnit,
    /** Именованная ячейка моста (`VAT_BOX1`); null — мост считает сам. */
    bridgeName: z.string().nullable().default(null),
});
export const AccountingFxRateDTO = z.object({
    provider: z.string().min(1),
    /** `2026-Q3` (ECB на конец квартала) или `2026-09` (HMRC месячный). */
    period: z.string().min(1),
    /** USD за 1 единицу валюты `quote`. */
    rate: AccountingDecimal,
    /** Валюта курса (`EUR`, `GBP`); null — не прислана (в `report.fx` её нет). */
    quote: z.string().nullable().default(null),
    /** За какую дату/месяц опубликован курс; null — не прислано. */
    publishedFor: z.string().nullable().default(null),
    /** Курс ещё не окончательный. */
    provisional: z.boolean(),
});
export const AccountingWarningDTO = z.object({
    /** Известные коды + любые новые коды платёжки (показ — по `message`). */
    code: z.string().min(1),
    message: z.string(),
    count: Count.nullable().default(null),
});
/**
 * Корректировка прошлого периода: возвраты в этом квартале по продажам
 * квартала `period` — вносятся в панель корректировок ROS. Суммы отрицательные.
 */
export const AccountingCorrectionDTO = z.object({
    /** Квартал исходных продаж. */
    period: AccountingQuarterId,
    country: z.string().min(1),
    /** Код для ROS (`EL` для Греции); null — совпадает со страной. */
    displayCode: z.string().nullable().default(null),
    label: z.string(),
    /** Ставка НДС в процентах. */
    rate: AccountingDecimal,
    refunds: Count,
    grossUsd: AccountingDecimal,
    gross: AccountingDecimal,
    net: AccountingDecimal,
    vat: AccountingDecimal,
});
export const AccountingRegionReportDTO = z.object({
    totals: AccountingReportTotalsDTO,
    rows: z.array(AccountingReportRowDTO),
    /** Боксы декларации (UK); null — у региона их нет. */
    boxes: z.array(AccountingReportBoxDTO).nullable().default(null),
    fx: z.array(AccountingFxRateDTO),
    warnings: z.array(AccountingWarningDTO),
    /** Корректировки прошлых периодов; null — нет (или регион без них). */
    corrections: z.array(AccountingCorrectionDTO).nullable().default(null),
});
export const AccountingReportInputsDTO = z.object({
    chargesCount: Count,
    refundsCount: Count,
    unresolvedRefunds: Count,
    unsupportedCurrency: Count,
    /**
     * Оплаты, захваченные меньше авторизованного; null — отчёт посчитан до
     * API 1.1.0 платёжки (поля нет).
     */
    partialCaptures: Count.nullable().default(null),
    /**
     * Самая свежая синхронизация зеркала Stripe; null — неизвестно / оплат нет.
     * Раньше конца квартала + 24 ч (или null) — отчёт устарел для подачи (`report_stale`).
     */
    mirrorSyncedAtMax: Iso.nullable().default(null),
    fx: z.array(AccountingFxRateDTO),
    /**
     * Ставки в процентах (`'21'`, `'25.5'`). Ключ — страна (`DE`) или
     * `страна:ставка` (`RO:19`) при смене ставки внутри квартала.
     */
    rates: z.record(z.string(), AccountingDecimal),
    fxProvisional: z.boolean(),
    /** Не учтённые оплаты (триалы, первые, разовые) — считаем только продления; null — не прислано. */
    excludedNonRenewal: z
        .object({ payments: Count, grossUsd: AccountingDecimal })
        .nullable()
        .default(null),
});
export const AccountingRegionReportEnvelopeDTO = z.object({
    region: AccountingRegionId,
    label: z.string().min(1),
    currency: z.string().min(1),
    filingHint: z.string(),
    status: AccountingRegionStatus,
    revision: Count.nullable().default(null),
    computedAt: Iso.nullable().default(null),
    /** `scheduler` | `admin:<id> <email>`. */
    computedBy: z.string().nullable().default(null),
    filedAt: Iso.nullable().default(null),
    filedBy: z.string().nullable().default(null),
    filingNote: z.string().nullable().default(null),
    inputs: AccountingReportInputsDTO.nullable().default(null),
    /** null — регион не посчитан. */
    report: AccountingRegionReportDTO.nullable().default(null),
});
export const AccountingQuarterDetailResponse = z.object({
    quarter: AccountingQuarterId,
    isCurrent: z.boolean(),
    prev: AccountingQuarterId.nullable().default(null),
    next: AccountingQuarterId.nullable().default(null),
    regions: z.array(AccountingRegionReportEnvelopeDTO),
});
// ---------------------------------------------------------------------------
// GET /quarters/:quarter/regions/:region/lines
// ---------------------------------------------------------------------------
export const AccountingLineListQuery = OffsetPageQuery.extend({
    type: AccountingLineType.optional(),
    country: AccountingCountry.optional(),
});
export const AccountingLineDTO = z.object({
    type: AccountingLineType,
    /** Stripe ID оплаты (`ch_…`) или возврата (`re_…`). */
    stripeId: z.string().min(1),
    chargeId: z.string().nullable().default(null),
    occurredAt: Iso,
    chargeCreatedAt: Iso.nullable().default(null),
    country: z.string().nullable().default(null),
    countrySource: AccountingCountrySource.nullable().default(null),
    customerCountry: z.string().nullable().default(null),
    billingCountry: z.string().nullable().default(null),
    cardCountry: z.string().nullable().default(null),
    /** Источники страны расходятся. */
    evidenceConflict: z.boolean(),
    kind: AccountingPaymentKind.nullable().default(null),
    /** Центы USD со знаком, как отдаёт платёжка. */
    amountUsdMinor: z.number().int(),
    /** Ставка НДС в процентах (`'21'`); null — у `unresolved`. */
    rate: AccountingDecimal.nullable().default(null),
    fxRate: AccountingDecimal.nullable().default(null),
    gross: AccountingDecimal.nullable().default(null),
    net: AccountingDecimal.nullable().default(null),
    vat: AccountingDecimal.nullable().default(null),
    revision: Count.nullable().default(null),
});
export const AccountingLineListResponse = offsetPage(AccountingLineDTO);
// ---------------------------------------------------------------------------
// POST /quarters/:quarter/recalculate
// ---------------------------------------------------------------------------
export const AccountingRecalculateRequest = z
    .object({
    /** Не задано — все регионы. */
    regions: z.array(AccountingRegionId).min(1).max(20).optional(),
})
    .strict();
export const AccountingRecalcResultDTO = z.object({
    region: AccountingRegionId,
    outcome: AccountingRecalcOutcome,
    revision: Count.nullable().default(null),
});
export const AccountingRecalculateResponse = z.object({
    results: z.array(AccountingRecalcResultDTO),
});
// ---------------------------------------------------------------------------
// POST /quarters/:quarter/regions/:region/file
// ---------------------------------------------------------------------------
export const ACCOUNTING_FILING_NOTE_MAX_LENGTH = 1000;
/**
 * Отметка «подан» необратима. Примечание (номер квитанции, кто подавал) —
 * необязательно; пустое после обрезки пробелов — как не заданное.
 *
 * `revision` — ОБЯЗАТЕЛЬНА: ревизия отчёта, которую оператор видел, когда
 * открыл диалог (оптимистичная блокировка). Пересчитан с тех пор — платёжка
 * отвечает 409 `revision_mismatch` и ничего не подаёт.
 */
export const AccountingFileRequest = z
    .object({
    revision: z
        .number({ message: 'Не передана ревизия отчёта — обновите страницу' })
        .int({ message: 'Ревизия отчёта — целое число' })
        .min(1, { message: 'Ревизия отчёта — не меньше 1' }),
    note: z
        .string()
        .trim()
        .max(ACCOUNTING_FILING_NOTE_MAX_LENGTH, {
        message: `Примечание — не длиннее ${ACCOUNTING_FILING_NOTE_MAX_LENGTH} символов`,
    })
        .optional()
        .transform((v) => (v === undefined || v.length === 0 ? undefined : v)),
})
    .strict();
export const AccountingFileResponse = AccountingRegionReportEnvelopeDTO;
//# sourceMappingURL=accounting.js.map